import { useServiceError } from '@/hooks';
import { notification } from 'antd';
import { useIntl } from 'react-intl';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import {
  createInvoiceNoteItems,
  deleteInvoiceNoteItems,
  getInvoiceNoteItemsId,
  invoiceNoteItems,
  updateInvoiceNoteItemsId,
} from './InvoiceNoteItems.service';
import {
  InvoiceItemCreateRequest,
  InvoiceItemUpdateRequest,
} from './InvoiceNoteItems.types';
import { queryClient } from '@/Root';
import notificationError from '@/utils/functions/notificationError';

export function useInvoiceNoteItems(id: number) {
  const query = useInfiniteQuery(['invoices', id], () => invoiceNoteItems(id), {
    getNextPageParam: (lastPage) => lastPage.nextToken,
  });

  useServiceError(query.error);

  return query;
}

export function useCreateInvoiceNoteItems() {
  const intl = useIntl();

  const mutation = useMutation(
    (obj: InvoiceItemCreateRequest) => createInvoiceNoteItems(obj),
    {
      async onSuccess(_, { invoiceId }) {
        await queryClient.invalidateQueries(['invoiceItems', { invoiceId }]);

        notification.success({
          message: intl.formatMessage({
            id: 'invoiceNoteItems.create.notifications.success.title',
          }),
          description: intl.formatMessage({
            id: 'invoiceNoteItems.create.notifications.success.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useDeleteInvoiceNoteItems(invoiceId: number) {
  const intl = useIntl();

  const queryClient = useQueryClient();

  const mutation = useMutation((id: number) => deleteInvoiceNoteItems(id), {
    async onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'invoiceNoteItems.create.notifications.success.title',
        }),
        description: intl.formatMessage({
          id: 'invoice.note.items.remove.notification.successDescription',
        }),
      });

      await queryClient.invalidateQueries(['invoiceItems', { invoiceId }]);
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useGetInvoiceNoteItems(invoiceId?: number) {
  const mutation = useQuery(
    ['invoice-items', invoiceId],
    () => getInvoiceNoteItemsId(invoiceId),
    {
      enabled: !!invoiceId,
    },
  );
  useServiceError(mutation.error);
  return mutation;
}

export function useInvoiceNoteItemDetail() {
  return useMutation(getInvoiceNoteItemsId, {
    onError(err) {
      notificationError(err);
    },
  });
}

export function useUpdateInvoiceNoteItemsId() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: InvoiceItemUpdateRequest & { hiddeNotification?: boolean }) =>
      updateInvoiceNoteItemsId(obj),
    {
      async onSuccess({ invoiceId }, { hiddeNotification }) {
        if (!hiddeNotification) {
          notification.success({
            message: 'Sucesso',
            description: intl({
              id: 'invoice.note.items.edit.notification.successDescription',
            }),
          });
        }

        await queryClient.invalidateQueries(['invoiceItems', { invoiceId }]);
      },
    },
  );
  useServiceError(mutation.error);
  return mutation;
}
